import { Avatar, Button, Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import {
  CurrentAssetWalletCounterparty,
  useCurrentAssetWallet,
} from 'src/Hooks/Shared/useCurrentAssetWallet';
import { SettlementTargetDto, SettlementTargetDtoTypeEnum } from 'src/Services/API/generated';

import styles from './MyBalanceCard.module.scss';

const MAX_ROWS = 3;

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

const formatFullName = (target: SettlementTargetDto): string => {
  if (target.type === SettlementTargetDtoTypeEnum.BankAccount) return target.name ?? '';
  const parts = [target.firstName, target.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return target.name ?? '';
};

const initialsOf = (target: SettlementTargetDto): string => {
  const first = target.firstName?.charAt(0) ?? '';
  const last = target.lastName?.charAt(0) ?? '';
  const combined = `${first}${last}`.toUpperCase();
  if (combined.length > 0) return combined;
  return (target.name ?? '?').charAt(0).toUpperCase();
};

const MyBalanceCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  // Mono-asset invariant: shared with the Finances "Mon solde" card so the two can never drift.
  const { owedToMe, iOwe, isSettled, owedToMeBy, iOweTo, isLoading } = useCurrentAssetWallet();

  const goToBalance = () => navigate(`/asset/${assetId}/expenses`);

  if (isLoading) {
    return (
      <div className={styles.card}>
        <h3 className={styles.title}>{t('dashboard.myBalance.title', 'Mon solde')}</h3>
        <Skeleton height={32} radius="sm" />
        <Skeleton height={14} radius="sm" width="60%" />
      </div>
    );
  }

  const owedAmount = owedToMe - iOwe;
  const isOwedToUser = owedAmount > 0;
  const userOwes = owedAmount < 0;

  const counterparties: CurrentAssetWalletCounterparty[] = isOwedToUser
    ? owedToMeBy
    : userOwes
      ? iOweTo
      : [];
  const sorted = [...counterparties].sort(
    (a, b) => Math.abs(b.netBalance) - Math.abs(a.netBalance)
  );
  const visible = sorted.slice(0, MAX_ROWS);
  const remaining = sorted.length - visible.length;

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>{t('dashboard.myBalance.title', 'Mon solde')}</h3>

      {isSettled && (
        <p className={styles.phrase}>{t('dashboard.myBalance.settledV2', 'Vous êtes à jour')}</p>
      )}
      {isOwedToUser && (
        <p className={`${styles.phrase} ${styles.owedPhrase}`}>
          {t('dashboard.myBalance.owed', 'On vous doit {{amount}}', {
            amount: formatCurrency(owedAmount),
          })}
        </p>
      )}
      {userOwes && (
        <p className={`${styles.phrase} ${styles.owePhrase}`}>
          {t('dashboard.myBalance.owe', 'Vous devez {{amount}}', {
            amount: formatCurrency(Math.abs(owedAmount)),
          })}
        </p>
      )}

      {!isSettled && visible.length > 0 && (
        <ul className={styles.people}>
          {visible.map(entry => (
            <li key={entry.target.id} className={styles.person}>
              <Avatar color="initials" size="md" name={initialsOf(entry.target)} />
              <span className={styles.name}>{formatFullName(entry.target)}</span>
              <span className={styles.amount}>{formatCurrency(Math.abs(entry.netBalance))}</span>
            </li>
          ))}
          {remaining > 0 && (
            <li className={styles.more}>
              {t('dashboard.myBalance.andOthers', {
                defaultValue: 'et {{count}} autre',
                defaultValue_other: 'et {{count}} autres',
                count: remaining,
              })}
            </li>
          )}
        </ul>
      )}

      <div className={styles.actions}>
        <Button variant="outline" size="md" onClick={goToBalance}>
          {t('dashboard.myBalance.cta', 'Voir mon solde')}
        </Button>
      </div>
    </div>
  );
};

export default MyBalanceCard;
